import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import moment from 'moment';

export default function CarSummary(props) {
  const { car } = props;

  const data = [
    {key:'year', type: 'text'},
    {key:'make', type: 'text'},
    {key:'model', type: 'text'},
    {key:'trim', type: 'text'},
    {key:'body-style', label: 'Body Style', type: 'select', values: constants.bodyStyles.map(x => ({label: formatTitle(x), value:x}))},
    {key:'door-count', label: 'Door Count', type: 'select', values: constants.doorCounts.map(x => ({label: x, value:`${x}`}))},
    {key:'color', type: 'text'},
    {key:'vin', type: 'text'},
    {key:'miles', type: 'text'},
    {key:'price', type: 'text'},
    {key:'engine-type', type: 'text'},
    {key:'cylinders', label: 'Cylinders', type: 'select', values: constants.cylinderCounts.map(x => ({label: x, value:`${x}`}))},
    {key:'interior-color', type: 'text'},
    {key:'interior-material', type: 'text'},
    {key:'transmission', label: 'Transmission', type: 'text'},
    {key:'drivetrain', label: 'Drivetrain', type: 'select', values: constants.drivetrains.map(x => ({label: formatTitle(x), value:x}))},
    {key:'location', label: 'Location', type: 'select', values: constants.makeSelects("locations")},
    {key:'is_actual', label: 'Actual Miles', type: 'check'},
    {key:'is_excess', label: 'Miles In Excess', type: 'check'},
    {key:'is_exempt', label: 'Miles Exempt', type: 'check'},
    {key:'odometer_5', label: '5-Digit Odometer', type: 'check'},
    {key:'odometer_6', label: '6-Digit Odometer', type: 'check'},
    {key:'ready_to_deliver', label: 'Ready to Deliver', type: 'check'},
    {key:'price_history', label: 'Price History', type: 'raw'},
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 4, md: 6 }, py: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Grid container spacing={3}>
        {data.map(item => (
          <Grid
            key={item.key}
            item
            xs={item.type === 'check' ? 6 : 12}
            sm={item.type === 'raw' ? 12 : item.type === 'check' ? 4 : 6}
          >
            <RenderInput item={item} car={car} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

const RenderInput = (props) => {
  const {item, car} = props;

  const updateCar = (data) => {
    const newCar = {...car, ...data};
    car.updater(newCar);
  };

  const [value, setValue] = React.useState(car[item.key]);

  const onChange = async (e) => {
    let {value, name, id} = e.target;
    value = value.trim();
    if ((id || name) === "vin") value = value.toUpperCase();
    const number_values = ["price"];
    if(number_values.includes(id)) value = +value;
    const data = {[id || name]: value};

    if (id === "price") {
      data.priceUpdated = true;
      const today = moment().format('YYYY/MM/DD');
      let updatedHistory = [];
      if (!car.price_history || car.price_history.length === 0) {
        const initialDate = car.status_time || moment().format('YYYY/MM/DD');
        updatedHistory.push({ price: car.price || 0, date: initialDate });
      } else {
        updatedHistory = [...car.price_history];
      }
      updatedHistory = updatedHistory.filter(entry => entry.date !== today);
      updatedHistory.push({ price: value, date: today });
      data.price_history = updatedHistory;
    }

    await firebase.firestore().collection('cars').doc(car.stock).set({...data}, {merge:true});
    setValue(value);
    updateCar(data);

    if ((id || name) === 'status' && ['active', 'success'].includes(value) && !car.activated_date) {
      const activatedDate = moment().format('YYYY/MM/DD');
      await firebase.firestore().collection('cars').doc(car.stock).set({ activated_date: activatedDate }, { merge: true });
      updateCar({ activated_date: activatedDate });
    }

    if(!!props.item.callback){
      props.item.callback({...e.target, docID: car.stock});
    }
  };

  const check = async (e) => {
    const data = {[e.target.id]: e.target.checked};
    await firebase.firestore().collection('cars').doc(car.stock).set(data, {merge:true});
    updateCar(data);
  };

  if(item.type === "text"){
    // Miles must be a clean number — strip commas, words, anything non-digit as
    // the user types so we never store "100,000" or "Exempt" (use the Miles
    // Exempt checkbox for that).
    const handleTextChange = (e) => {
      let v = e.target.value;
      if (item.key === "miles") v = v.replace(/[^\d]/g, '');
      if (item.key === "vin") v = v.toUpperCase();
      setValue(v);
    };
    return (
      <TextField
        value={value || ''}
        onChange={handleTextChange}
        onBlur={onChange}
        id={item.key}
        name={item.key}
        label={item.label || formatTitle(item.key)}
        fullWidth
        inputProps={
          item.key === "vin" ? { style: { textTransform: 'uppercase' } }
          : item.key === "miles" ? { inputMode: 'numeric', pattern: '[0-9]*' }
          : undefined
        }
      />
    );
  } else if (item.type === "select") {
    return (
      <FormControl fullWidth required>
        <InputLabel id={item.key}>{item.label || formatTitle(item.key)}</InputLabel>
        <Select
          labelId={item.key}
          id={item.key}
          name={item.key}
          value={value || ''}
          onChange={onChange}
        >
          {item.values.map(selection => (
            <MenuItem key={selection.value} value={selection.value}>{selection.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  } else if (item.type === "check") {
    return (
      <FormControlLabel
        control={<Checkbox id={item.key} defaultChecked={car[item.key]} onClick={check} />}
        label={item.label}
        sx={{ textAlign: 'left' }}
      />
    );
  } else if (item.type === "raw") {
    const rawValue = car[item.key];

    if (item.key === 'price_history' && Array.isArray(rawValue)) {
      const sortedHistory = [...rawValue].sort((a, b) =>
        moment(a.date, 'YYYY/MM/DD') - moment(b.date, 'YYYY/MM/DD')
      );
      return (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            {item.label || formatTitle(item.key)}
          </Typography>
          {sortedHistory.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedHistory.map((entry, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        {moment(entry.date, 'YYYY/MM/DD').format('MMM DD, YYYY')}
                      </TableCell>
                      <TableCell align="right">
                        {entry.price.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
              No price history available
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <TextField
        value={rawValue ? JSON.stringify(rawValue, null, 2) : ''}
        label={item.label || formatTitle(item.key)}
        fullWidth
        multiline
        rows={4}
        InputProps={{ readOnly: true }}
      />
    );
  }
};

const formatTitle = raw => {
  raw = raw.split('_').join(' ');
  if(raw.includes("-")){
    raw = raw.split('-').join(' ');
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};
